/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import { Address, Cart, CartsDataActions } from '@msdyn365-commerce/retail-proxy';

import { ICartActionResultWithCart } from './cart-action-result';

/**
 * Update shipping address in cart.
 * @param cart - The cart object.
 * @param shippingAddress - The new shipping address.
 * @param actionContext - The action context.
 * @returns The updated cart with new shipping address.
 */
export async function updateShippingAddress(
    cart: Readonly<Cart | undefined>,
    shippingAddress: Address,
    actionContext: IActionContext
): Promise<ICartActionResultWithCart> {
    if (!cart) {
        return { cart: undefined, status: 'FAILED', substatus: 'EMPTYCART' };
    }

    return CartsDataActions.updateAsync(
        { callerContext: actionContext },
        {
            Id: cart.Id,
            ShippingAddress: shippingAddress
        }
    )
        .then(newCart => {
            const result: ICartActionResultWithCart = { cart: newCart, status: 'SUCCESS' };
            return result;
        })
        .catch(error => {
            actionContext.telemetry.warning(error);
            actionContext.telemetry.debug('Unable to update shipping address to the cart');

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- existing error type.
            return { cart: undefined, status: 'FAILED', errorDetails: { LocalizedMessage: error.message } };
        });
}

export default updateShippingAddress;
